// Wheel-level Video/GIF Portrait — canonical media contract, tier gate, and
// upload validation (WL-056; DESIGN-DECISIONS-LOG rows 17 / 30 / 40).
//
// This module is the SINGLE SOURCE OF TRUTH the builder UI and the e2e specs
// share. It is a MIRROR of the backend gate (yuga_wheel/services/portrait_media.py):
// keep the tier map and caps identical in both. The client gate is UX-only —
// the server re-checks the same rules and FAILS CLOSED (see backend).
//
// Portrait media contract (matches renderer-v2 wheelDoc portrait.fill):
//   { type: 'image' | 'gif' | 'video', source: <url>, poster?: <url> }
// The v2 engine (src/renderer-v2/engine.js renderPortrait) already renders each
// type with offscreen-pause + reduced-motion poster fallback; this file governs
// what a given subscription tier is ALLOWED to author.

// PackageType.sub_tier (backend subscription/enums.py): FREE 0, ESSENTIAL 1,
// PREMIUM 2, PRO_SUITE 3.
export const SUB_TIER = Object.freeze({
  FREE: 0,
  ESSENTIAL: 1,
  PREMIUM: 2,
  PROSUITE: 3,
});

export const PORTRAIT_MEDIA_TYPES = Object.freeze(["image", "gif", "video"]);

// Minimum tier required to AUTHOR each portrait media type.
//   image → Free (row: static portrait is baseline, everyone)
//   gif   → Essential (row 30: "GIFs selectable in Wheel Portrait = Essential (E)")
//   video → ProSuite (row 17: "per-segment video = ProSuite … wheel-level video v1")
export const PORTRAIT_MEDIA_TIER = Object.freeze({
  image: SUB_TIER.FREE,
  gif: SUB_TIER.ESSENTIAL,
  video: SUB_TIER.PROSUITE,
});

// Tier display names + the badge that REPLACES the tier word on the toggle
// (DESIGN-DECISIONS-LOG row 28: no-redundancy badge rule — a badge replaces the
// tier word wherever both would appear). Badge asset keys map to the SVGs in
// src/assets/images (essentialPackageIcon.svg / ProSuite.svg); "" = no badge.
export const TIER_NAME = Object.freeze({
  0: "Free",
  1: "Essential",
  2: "Premium",
  3: "ProSuite",
});

export const TIER_BADGE = Object.freeze({
  1: "E", //  Essential
  3: "Ps", // ProSuite
});

// Upload caps. Video: ≤50MB, no duration limit (Gordon decision 2026-10-08:
// Kelly's 43MB clip was blocked at the old 25MB cap, raised to 50MB; rows
// 17/40; deck VideoSettings formats. Kelly + Gordon decision A, 2026-10-08,
// supersedes the earlier same-day "trim to 15s" call — portrait videos have
// NO duration limit, only this size cap). GIF: ≤8MB (deck state 04: "GIF up
// to 8MB · loops automatically").
export const VIDEO_MAX_BYTES = 50 * 1024 * 1024; // 50MB
export const GIF_MAX_BYTES = 8 * 1024 * 1024; //     8MB
export const VIDEO_FORMATS = Object.freeze(["mp4", "mov", "avi", "mkv"]);
export const GIF_FORMATS = Object.freeze(["gif"]);

// Allowed MIME types, used ONLY as a fallback when a filename has no extension
// (e.g. a recorded Blob). The extension allowlist is otherwise authoritative —
// a `video/webm` named `clip.webm` must be rejected even though its MIME starts
// with `video/`.
const VIDEO_MIME = Object.freeze([
  "video/mp4",
  "video/quicktime", // mov
  "video/x-msvideo", // avi
  "video/x-matroska", // mkv
]);

const isFiniteNum = (n) => typeof n === "number" && Number.isFinite(n);
const extOf = (name) =>
  typeof name === "string" && name.includes(".")
    ? name.split(".").pop().toLowerCase()
    : "";

/**
 * Resolve whether a subscription tier may author a portrait media type.
 * FAIL CLOSED: an unknown media type, a non-numeric tier, or the feature flag
 * being off all return { allowed: false } — the caller must never open the
 * option on a truthy default.
 *
 * @param {string} mediaType   one of PORTRAIT_MEDIA_TYPES
 * @param {number} subTier     the user's active PackageType.sub_tier
 * @param {object} [opts]
 * @param {boolean} [opts.flagEnabled=true]  VIDEO_PORTRAIT_ENABLED
 * @returns {{allowed:boolean, mediaType:string, requiredTier:number|null,
 *            requiredTierName:string, badge:string, reason:string}}
 */
export function resolvePortraitMediaGate(mediaType, subTier, opts = {}) {
  const flagEnabled = opts.flagEnabled !== false; // default true
  const base = {
    allowed: false,
    mediaType,
    requiredTier: null,
    requiredTierName: "",
    badge: "",
    reason: "",
  };

  // Image is always available (baseline) — but the animated toggle itself is
  // flag-gated, so image outside the flag is still "allowed" (it is the legacy
  // static portrait). Only gif/video are new, flag-gated surfaces.
  if (mediaType === "image") {
    return { ...base, allowed: true, requiredTier: SUB_TIER.FREE, requiredTierName: TIER_NAME[0] };
  }

  if (!flagEnabled) {
    return { ...base, reason: "flag-off" };
  }

  if (!Object.prototype.hasOwnProperty.call(PORTRAIT_MEDIA_TIER, mediaType)) {
    return { ...base, reason: "unknown-type" }; // fail closed
  }
  if (!isFiniteNum(subTier)) {
    const required = PORTRAIT_MEDIA_TIER[mediaType];
    return {
      ...base,
      requiredTier: required,
      requiredTierName: TIER_NAME[required] || "",
      badge: TIER_BADGE[required] || "",
      reason: "unknown-tier", // fail closed — treat as not entitled
    };
  }

  const required = PORTRAIT_MEDIA_TIER[mediaType];
  const allowed = subTier >= required;
  return {
    allowed,
    mediaType,
    requiredTier: required,
    requiredTierName: TIER_NAME[required] || "",
    badge: TIER_BADGE[required] || "",
    reason: allowed ? "ok" : "tier-too-low",
  };
}

/**
 * Validate an uploaded portrait VIDEO. Returns { valid, errors:[] }.
 * Enforces format and ≤50MB only — stgfix-1008-T311b-r2 (Kelly + Gordon
 * decision A, 2026-10-08): no duration limit on portrait videos.
 *
 * @param {{name?:string, size?:number, type?:string}} file
 */
export function validatePortraitVideoFile(file) {
  const errors = [];
  if (!file || typeof file !== "object") {
    return { valid: false, errors: ["No file provided."] };
  }
  const ext = extOf(file.name);
  // Extension is authoritative when present; MIME is a fallback only when the
  // name has no extension (so a disallowed ext like .webm is never rescued by
  // a permissive `video/*` MIME).
  const typeOk = ext
    ? VIDEO_FORMATS.includes(ext)
    : VIDEO_MIME.includes(file.type || "");
  if (!typeOk) {
    errors.push(`Unsupported format. Use ${VIDEO_FORMATS.join(", ")}.`);
  }
  if (isFiniteNum(file.size) && file.size > VIDEO_MAX_BYTES) {
    errors.push(`Video is too large (max ${VIDEO_MAX_BYTES / (1024 * 1024)}MB).`);
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate an uploaded portrait GIF. Returns { valid, errors:[] }.
 * Enforces format + ≤8MB. (Seamless-loop is a supply guarantee, not a
 * client-measurable check — see row 30.)
 */
export function validatePortraitGifFile(file) {
  const errors = [];
  if (!file || typeof file !== "object") {
    return { valid: false, errors: ["No file provided."] };
  }
  const ext = extOf(file.name);
  const typeOk = ext ? GIF_FORMATS.includes(ext) : (file.type || "") === "image/gif";
  if (!typeOk) {
    errors.push("Unsupported format. Use a .gif file.");
  }
  if (isFiniteNum(file.size) && file.size > GIF_MAX_BYTES) {
    errors.push(`GIF is too large (max ${GIF_MAX_BYTES / (1024 * 1024)}MB).`);
  }
  return { valid: errors.length === 0, errors };
}

/**
 * A video portrait REQUIRES a poster (still frame) so reduced-motion / offscreen
 * fallback is never empty (§12 center law: portrait is NEVER empty). Returns
 * true when the portrait fill is complete enough to save.
 */
export function isPortraitFillComplete(fill) {
  if (!fill || typeof fill !== "object") return false;
  if (fill.type === "video") return !!fill.source && !!fill.poster;
  if (fill.type === "gif" || fill.type === "image") return !!fill.source;
  return false;
}

// ---------------------------------------------------------------------------
// Renderer adapters — the SINGLE derivation every v1 surface shares.
//
// WHY THESE EXIST (ISP-9999, wave33): the v1 D3 engine draws the center portrait
// as an HTML <img>, so a VIDEO url handed to it silently fails to decode
// (naturalWidth/Height 0) and paints a hollow white circle — a §12 center-law
// violation. WheelComponent already fixes this by mounting PortraitVideoOverlay
// when it receives a `centerMedia` prop, but each surface was deriving that prop
// (and the <img> url) by hand: ViewWheel did it right, the Builder never did it
// at all. MEASURED on web-stg 2026-07-25, builder/2889: <img> with the .mp4 as
// its src, naturalWidth 0 — against GIF 245 / image 322 on the same probe.
//
// Both renderers now call these two functions, so the two paths CANNOT diverge
// again. Detection is by the canonical `type` discriminator the working
// ViewWheel path already used — no new heuristic, no extension sniffing.
// ---------------------------------------------------------------------------

/**
 * API `wheel_center_media` { type, url, poster } -> the WheelComponent
 * `centerMedia` contract { type:'video', source, poster }, or `undefined`.
 *
 * `undefined` is returned for EVERYTHING that is not a complete video (flag off,
 * absent media, image, gif, or a video with no source) so a non-video wheel
 * renders byte-identically to before — WheelComponent only mounts the overlay on
 * a truthy video centerMedia. A GIF is deliberately excluded: it already animates
 * on the D3 <img> path, so routing it through the overlay would double-fetch for
 * zero user benefit (see PortraitVideoOverlay's header).
 *
 * @param {{type?:string,url?:string,poster?:string}} [centerMedia]
 * @param {{flagEnabled?:boolean}} [opts]  flagEnabled=false => always undefined
 * @returns {{type:'video',source:string,poster:string}|undefined}
 */
export function resolvePortraitCenterMedia(centerMedia, opts = {}) {
  if (opts.flagEnabled === false) return undefined;
  if (!centerMedia || typeof centerMedia !== "object") return undefined;
  if (centerMedia.type !== "video") return undefined;
  if (!centerMedia.url) return undefined;
  return { type: "video", source: centerMedia.url, poster: centerMedia.poster || "" };
}

/**
 * The url that is safe to hand to the v1 D3 engine's center-portrait <img>.
 *
 * §12 center law — the wheel center is NEVER bare/hollow. A VIDEO url must never
 * reach an <img> (it cannot decode), so a video resolves to its poster still, and
 * failing that to the caller's fallback (the iYuga logo). Image/GIF are returned
 * unchanged; an absent/empty source falls back too.
 *
 * @param {{type?:string,url?:string,poster?:string}} [centerMedia]
 * @param {string} [fallbackUrl]  e.g. ASSETS.wheelLogoFallback
 * @returns {string}
 */
export function resolvePortraitStillUrl(centerMedia, fallbackUrl = "") {
  if (!centerMedia || typeof centerMedia !== "object") return fallbackUrl;
  if (centerMedia.type === "video") return centerMedia.poster || fallbackUrl;
  return centerMedia.url || fallbackUrl;
}
